/**
 * Dynamic panel dimensions.
 *
 * A panel dimension may be linked to its component's depth or height. The
 * stored number is the panel's BASE; the effective size shifts by the same
 * amount the linked component dimension has shifted from ITS base:
 *
 *   effective = max(0, base_panel + (live_X - (base_X ?? live_X)))
 *
 * The delta is additive, not proportional, so authored offsets survive a
 * resize: a panel cut at `depth - 0.75` stays `depth - 0.75` at any depth.
 */

/** Every axis a dimension may track. */
export type DimAxis = 'width' | 'depth' | 'height';

/** Normalized dimensions. Deliberately not the raw config shape — see componentDims. */
export interface ComponentDims {
  /** Inches. lf_quantity * 12 on LF components, the plain field on an arm. */
  width: number;
  depth: number;
  height: number;
  /**
   * The axis a rail runs along. An arm's rail runs front to back (depth); every
   * LF component's runs along the width. Required rather than optional so a
   * call site that forgets it fails to compile instead of silently mispricing
   * arms by a factor of four.
   */
  run_axis: DimAxis;
  base_width?: number | null;
  base_depth?: number | null;
  base_height?: number | null;
}

export interface ResolvedDim {
  /** Clamped, safe to use in arithmetic. */
  value: number;
  /** Unclamped result, for diagnostics. */
  raw: number;
  /** True when the panel has no size and will not be produced. */
  collapsed: boolean;
}

/** The raw config shapes this module accepts. */
export interface ComponentDimSource {
  profile_depth?: number | null;
  profile_height?: number | null;
  width?: number | null;
  depth?: number | null;
  height?: number | null;
  lf_quantity?: number | null;
  product_type?: string | null;
  base_width?: number | null;
  base_depth?: number | null;
  base_height?: number | null;
}

/**
 * The single place that knows ArmConfigSchema uses `depth`/`height` while the
 * other three configs use `profile_depth`/`profile_height`. Reading
 * `config.profile_depth` directly yields `undefined` on an arm, which produces a
 * zero delta and silently disables the feature — always go through this.
 */
export function componentDims(config?: ComponentDimSource | null): ComponentDims {
  const cfg = config ?? {};
  return {
    // ArmConfigSchema carries a plain `width`; base/seat/back carry `lf_quantity`
    // and no width field at all. No config has both, so this is unambiguous.
    // Reading either one directly at a call site yields undefined on the other
    // shape, which produces a zero delta and silently disables the feature.
    width: cfg.width ?? (cfg.lf_quantity ?? 0) * 12,
    depth: cfg.profile_depth ?? cfg.depth ?? 0,
    height: cfg.profile_height ?? cfg.height ?? 0,
    // An arm's rail runs front to back; every LF component's runs along the width.
    run_axis: cfg.product_type === 'banquette_arm' ? 'depth' : 'width',
    base_width: cfg.base_width ?? null,
    base_depth: cfg.base_depth ?? null,
    base_height: cfg.base_height ?? null,
  };
}

export function resolvePanelDim(
  base: number,
  link: DimAxis | null | undefined,
  dims: ComponentDims,
): ResolvedDim {
  const safeBase = Number.isFinite(base) ? base : 0;
  if (!link) return { value: safeBase, raw: safeBase, collapsed: false };

  const live =
    link === 'width' ? dims.width : link === 'depth' ? dims.depth : dims.height;
  // A null base means "never baselined" — treat it as the live value so the
  // delta is exactly zero and the panel behaves as static.
  const baseline =
    (link === 'width'
      ? dims.base_width
      : link === 'depth'
        ? dims.base_depth
        : dims.base_height) ?? live;

  const raw = safeBase + (live - baseline);
  return { value: Math.max(0, raw), raw, collapsed: raw <= 0 };
}

/** A rail's two dimensions, as stored. */
export interface RailDims {
  width?: number;
  width_link?: DimAxis | null;
  length?: number | null;
  length_link?: DimAxis | null;
}

/**
 * A rail's second dimension.
 *
 * A null (or absent) length is the legacy encoding for "spans the run" and
 * resolves to the live run axis — exactly the value consumers used to compute
 * inline as `lf_quantity * 12`, or `depth` on an arm. It has no base and no
 * delta: it simply IS the run.
 */
export function resolveRailLength(rail: RailDims, dims: ComponentDims): ResolvedDim {
  if (rail?.length == null) {
    const live = dims[dims.run_axis];
    return { value: Math.max(0, live), raw: live, collapsed: live <= 0 };
  }
  return resolvePanelDim(rail.length, rail.length_link, dims);
}

/**
 * The link a rail's length should DISPLAY as. A legacy rail reads as linked to
 * the run axis, so the row shows two explicit values and nothing reveals that
 * the stored value is absent.
 */
export function railLengthLink(rail: RailDims, dims: ComponentDims): DimAxis | null {
  return rail?.length == null ? dims.run_axis : (rail.length_link ?? null);
}

interface LinkedRail { width?: number; width_link?: DimAxis | null }
interface LinkedPanel {
  width?: number; height?: number;
  width_link?: DimAxis | null; height_link?: DimAxis | null;
}

/**
 * Returns the same framing shape with rails and additional_panels carrying
 * resolved numbers, so every downstream consumer keeps reading plain values.
 */
export function resolveFramingDims<T extends Record<string, any>>(
  framing: T,
  dims: ComponentDims,
): T {
  if (!framing) return framing;

  const out: Record<string, any> = { ...framing };

  if (Array.isArray(framing.rails)) {
    out.rails = framing.rails.map((rail: LinkedRail & RailDims) => ({
      ...rail,
      width: resolvePanelDim(rail?.width ?? 0, rail?.width_link, dims).value,
      length: resolveRailLength(rail, dims).value,
    }));
  }

  if (Array.isArray(framing.additional_panels)) {
    out.additional_panels = framing.additional_panels.map((panel: LinkedPanel) => ({
      ...panel,
      width: resolvePanelDim(panel?.width ?? 0, panel?.width_link, dims).value,
      height: resolvePanelDim(panel?.height ?? 0, panel?.height_link, dims).value,
    }));
  }

  return out as T;
}

// ── Foam layers ──
//
// Foam differs from panels in two ways: it may track `width`, and on the three
// LF components its length has an Auto state that reproduces the pre-feature
// behavior (the layer runs the component's full width).

// `name` and `material_id` are carried explicitly rather than left to the
// generic. Callers whose config is `any` infer T as the constraint itself, and
// the Omit in the return type would then drop any field not named here.
export interface RunFoamLayerDims {
  name?: string;
  material_id?: string | null;
  width?: number;
  width_link?: DimAxis | null;
  /** null or absent means Auto: the layer runs the component's full width. */
  length?: number | null;
  length_link?: DimAxis | null;
}

export interface ArmFoamLayerDims {
  name?: string;
  material_id?: string | null;
  width?: number;
  width_link?: DimAxis | null;
  depth?: number;
  depth_link?: DimAxis | null;
}

/**
 * Base, seat cushion, back cushion. Returns the same layer objects carrying
 * resolved `width` and `length`, so downstream loops keep reading plain numbers.
 *
 * `collapsed` is computed here rather than taken from resolvePanelDim because a
 * STATIC zero is also collapsed — resolvePanelDim reports collapsed:false for an
 * unlinked dimension, which is right for panels but wrong as a nesting guard.
 */
export function resolveFoamLayers<T extends RunFoamLayerDims>(
  layers: T[] | null | undefined,
  dims: ComponentDims,
): Array<Omit<T, 'width' | 'length'> & { width: number; length: number; collapsed: boolean }> {
  if (!Array.isArray(layers)) return [];
  return layers.map((layer) => {
    const w = resolvePanelDim(layer?.width ?? 0, layer?.width_link, dims);
    // Auto has no base and no delta: the length simply IS the component width.
    const length =
      layer?.length == null
        ? dims.width
        : resolvePanelDim(layer.length, layer.length_link, dims).value;
    return {
      ...layer,
      width: w.value,
      length,
      collapsed: w.value <= 0 || length <= 0,
    };
  });
}

/** Arm. No run and therefore no length and no Auto state. */
export function resolveArmFoamLayers<T extends ArmFoamLayerDims>(
  layers: T[] | null | undefined,
  dims: ComponentDims,
): Array<Omit<T, 'width' | 'depth'> & { width: number; depth: number; collapsed: boolean }> {
  if (!Array.isArray(layers)) return [];
  return layers.map((layer) => {
    const w = resolvePanelDim(layer?.width ?? 0, layer?.width_link, dims);
    const d = resolvePanelDim(layer?.depth ?? 0, layer?.depth_link, dims);
    return {
      ...layer,
      width: w.value,
      depth: d.value,
      collapsed: w.value <= 0 || d.value <= 0,
    };
  });
}
